import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  PlainButton,
  TextInput,
} from "@jugl-web/ui-components/cross-platform";
import { useUserAuth } from "@web-src/modules/auth/providers/UserAuthProvider/useUserAuth";
import { useConfirmationDialogState } from "@jugl-web/utils/hooks/useConfirmationDialogState";
import {
  cx,
  isDeleteAccountApiError,
  useToast,
  useTranslations,
} from "@jugl-web/utils";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { useMe } from "@web-src/features/app/hooks/useMe";
import { useSelector } from "react-redux";
import { selectAuthState } from "@web-src/features/auth/authSlice";
import { Tooltip } from "@jugl-web/ui-components";
import { ReactComponent as WarningIcon } from "./assets/warning.svg";
import { ManageWorkspaceAdminsDialog } from "../ManageWorkspaceAdminsDialog";
import { useDeleteAccountDialog } from "./useDeleteAccountDialog";
import { ReactComponent as InfoIcon } from "./assets/info.svg";
import { ReactComponent as DarkInfoIcon } from "./assets/dark-info.svg";

export const DeleteAccountDialog: React.FC = () => {
  const { usersApi } = useRestApiProvider();
  const { isAuthenticated } = useSelector(selectAuthState);
  const { logoutSideEffects } = useUserAuth();
  const { toast } = useToast();
  const { t } = useTranslations();
  const { me } = useMe();
  const { isOpen, close } = useDeleteAccountDialog();

  const manageWorkspaceAdminsDialogState = useConfirmationDialogState<{
    entities: string[];
  }>();

  const [
    deleteUserConfirmationInputValue,
    setDeleteUserConfirmationInputValue,
  ] = useState<string>();

  const inputRef = useRef<HTMLInputElement>(null);

  const [deleteMyAccount, { isLoading: isDeleteMyAccountLoading }] =
    usersApi.useDeleteMyAccountMutation();

  useEffect(() => {
    if (isOpen) {
      setDeleteUserConfirmationInputValue("");
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const handleDeleteMyAccount = async () => {
    const resp = await deleteMyAccount();
    if ("error" in resp && resp.error) {
      if (
        isDeleteAccountApiError(resp) &&
        resp.error.data.errors.error === "last_admin_cannot_leave"
      ) {
        close();
        manageWorkspaceAdminsDialogState.open({
          metadata: {
            entities: resp.error.data.errors.reason.entity_ids,
          },
        });
        return;
      }

      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong",
        }),
        { variant: "error" }
      );
      return;
    }
    toast(
      t({
        id: "feedback.your account was deleted",
        defaultMessage: "Your Account was deleted",
      })
    );
    close();
    manageWorkspaceAdminsDialogState.close();
    logoutSideEffects();
  };

  if (!isAuthenticated) return null;
  return (
    <>
      <Alert
        className="font-secondary"
        isOpen={isOpen}
        header={t({
          id: "common.confirm-action",
          defaultMessage: "Confirm action",
        })}
        isCloseButtonVisible
        headerRightContent={
          <Tooltip
            className="font-secondary w-[264px] p-3 text-center"
            zIndex={2000}
            renderTrigger={({ props, ref, isOpen: isTooltipOpen }) => (
              <PlainButton
                ref={ref}
                {...props}
                className={cx(
                  "group/alertInfo mr-4 flex h-[32px] w-[32px] items-center justify-center rounded-lg transition-all duration-200",
                  {
                    "bg-grey-100": isTooltipOpen,
                  }
                )}
              >
                {isTooltipOpen ? <DarkInfoIcon /> : <InfoIcon />}
              </PlainButton>
            )}
          >
            {t({
              id: "delete-account-dialog-component.info-tooltip",
              defaultMessage:
                "After deleting Your Account, all of the associated login methods will no longer be valid ℹ️ ",
            })}
          </Tooltip>
        }
        onRequestClose={close}
        img={
          <div className="relative">
            <Avatar username={me?.display_name || ""} size="5xl" />
            <div className="absolute bottom-0 left-[90px] h-[60px] w-[60px] rounded-full bg-white p-2.5">
              <WarningIcon />
            </div>
          </div>
        }
        title={t({
          id: "delete-account-dialog-component.title",
          defaultMessage: "We are sad to say Goodbye 💔",
        })}
        content={
          <div className="flex flex-col gap-8 text-center">
            <div>
              {t(
                {
                  id: "delete-account-dialog-component.description",
                  defaultMessage:
                    "You are trying to delete your entire Account. By doing this, you will <u>lose access to all Workspaces</u>, that are associated to this Account. <b>This action cant be undone.</b>",
                },
                {
                  u: (text: (string | JSX.Element)[]) => (
                    <span className="italic underline">{text}</span>
                  ),
                  b: (text: (string | JSX.Element)[]) => (
                    <span className="font-bold">{text}</span>
                  ),
                }
              )}
            </div>
            <TextInput
              ref={inputRef}
              value={deleteUserConfirmationInputValue}
              classNames={{
                wrapperClassName: "flex flex-col",
              }}
              placeholder={t({
                id: "common.enter",
                defaultMessage: "Enter",
              })}
              label={t({
                id: "delete-account-dialog-component.input-label",
                defaultMessage: "Type “DELETE” to confirm an action",
              })}
              onChange={(e) =>
                setDeleteUserConfirmationInputValue(
                  e.target.value.toUpperCase()
                )
              }
            />
          </div>
        }
        buttonsContainerClassName="w-[300px] mx-auto mt-[56px]"
        buttons={[
          {
            text: t({
              id: "delete-account-dialog-component.delete-my-account",
              defaultMessage: "Delete My Account",
            }),
            color: "tertiary",
            className: "h-[40px]",
            isLoading: isDeleteMyAccountLoading,
            isDisabled: deleteUserConfirmationInputValue !== "DELETE",
            onClick: () => {
              handleDeleteMyAccount();
            },
          },
        ]}
      />
      <ManageWorkspaceAdminsDialog
        isOpen={manageWorkspaceAdminsDialogState.isOpen}
        onClose={manageWorkspaceAdminsDialogState.close}
        entityIds={manageWorkspaceAdminsDialogState.metadata?.entities}
        onDelete={handleDeleteMyAccount}
      />
    </>
  );
};
